import { ensureTrailingSlash, splitOnQuestionMark } from '@sleepstation/utils'
import { concat, converge, head, isNil, pipe } from 'ramda'
import { compact, concatAll, isFalse, list } from 'ramda-adjunct'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const EXCLUDE = `exclude`

const ROOT = `https://www.sleepstation.org.uk`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const processUrl = pipe(
  splitOnQuestionMark,
  head,
  ensureTrailingSlash,
  concat(ROOT)
)

const basicMetadata = ({ description, shouldIndex }) => {
  if (isFalse(shouldIndex)) {
    return [
      {
        name: `robots`,
        content: `noindex`,
      },
    ]
  }

  const metadata = [
    description && {
      name: `description`,
      content: description,
    },
  ]

  return compact(metadata)
}

// -----------------------------------------------------------------------------
// Google
// -----------------------------------------------------------------------------

const googleProductMetadata = ({ title, description, imageUrl }) => [
  {
    itemprop: `name`,
    content: title,
  },
  {
    itemprop: `description`,
    content: description,
  },
  {
    itemprop: `image`,
    content: imageUrl,
  },
]

// -----------------------------------------------------------------------------
// Facebook
// -----------------------------------------------------------------------------

const facebookMetadata = ({ title, description, imageUrl, pathPath }) => [
  {
    property: `og:url`,
    content: pathPath,
  },
  {
    property: `og:type`,
    content: `website`,
  },
  {
    property: `og:title`,
    content: title,
  },
  {
    property: `og:description`,
    content: description,
  },
  {
    property: `og:image`,
    content: imageUrl,
  },
]

// -----------------------------------------------------------------------------
// Twitter
// -----------------------------------------------------------------------------

const twitterMetadata = ({ title, description, imageUrl }) => [
  {
    name: `twitter:card`,
    content: `summary_large_image`,
  },
  {
    name: `twitter:title`,
    content: title,
  },
  {
    name: `twitter:description`,
    content: description,
  },
  {
    name: `twitter:image`,
    content: imageUrl,
  },
]

const metadata = [
  basicMetadata,
  googleProductMetadata,
  facebookMetadata,
  twitterMetadata,
]

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

export const createMetadata = converge(pipe(list, concatAll), metadata)

export const createLinks = ({ canonical, pagePath }) => {
  // A page can opt out by setting the value of canonical to 'exclude'.
  // Otherwise it will be opted in with the href set to itself.
  if (canonical === EXCLUDE) return {}

  const candidateUrl = isNil(canonical) ? pagePath : canonical
  const href = processUrl(candidateUrl)

  return [
    {
      rel: `canonical`,
      href,
    },
  ]
}
